import {
  TaxConfiguration,
  CreateTaxConfigRequest,
  TaxConfigurationHistory,
  TaxChangeNotification,
  TaxRateCalculationResponse,
  TaxConfigSearchParams,
  PaginatedResponse,
} from '../types/taxConfig';
import { get, post, put } from '../utils/httpClient';

const BASE_PATH = '/api/v1/admin/tax-configurations';

async function parseErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.json();
    return data.error || data.message || fallback;
  } catch {
    return fallback;
  }
}

function buildQueryString(params: TaxConfigSearchParams): string {
  const query = new URLSearchParams();
  if (params.search) query.set('search', params.search);
  if (params.scope) query.set('scope', params.scope);
  if (params.active_only !== undefined) query.set('active_only', String(params.active_only));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.offset !== undefined) query.set('offset', String(params.offset));
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

/** Fetch tax configurations with optional filters */
export async function fetchTaxConfigurations(
  params: TaxConfigSearchParams = {}
): Promise<PaginatedResponse<TaxConfiguration>> {
  const qs = buildQueryString({ limit: 50, ...params });
  const response = await get(`${BASE_PATH}${qs}`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors du chargement des configurations fiscales');
    throw new Error(msg);
  }
  return response.json();
}

/** Create a new tax configuration */
export async function createTaxConfiguration(data: CreateTaxConfigRequest): Promise<TaxConfiguration> {
  const response = await post(BASE_PATH, data);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors de la création de la configuration');
    throw new Error(msg);
  }
  return response.json();
}

/** Update an existing tax configuration */
export async function updateTaxConfiguration(
  id: string,
  data: CreateTaxConfigRequest
): Promise<TaxConfiguration> {
  const response = await put(`${BASE_PATH}/${id}`, data);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors de la mise à jour de la configuration');
    throw new Error(msg);
  }
  return response.json();
}

/** Deactivate a tax configuration */
export async function deactivateTaxConfiguration(id: string): Promise<void> {
  const response = await post(`${BASE_PATH}/${id}/deactivate`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors de la désactivation');
    throw new Error(msg);
  }
}

/** Calculate applicable tax rate for a given context */
export async function calculateTaxRate(
  province?: string,
  hotelCategory?: string,
  date?: string
): Promise<TaxRateCalculationResponse> {
  const query = new URLSearchParams();
  if (province) query.set('province', province);
  if (hotelCategory) query.set('hotel_category', hotelCategory);
  if (date) query.set('date', date);
  const qs = query.toString();
  const response = await get(`${BASE_PATH}/calculate${qs ? '?' + qs : ''}`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors du calcul du taux');
    throw new Error(msg);
  }
  return response.json();
}

/** Get change history for a specific tax configuration */
export async function fetchConfigHistory(
  configId: string
): Promise<{ data: TaxConfigurationHistory[]; total: number }> {
  const response = await get(`${BASE_PATH}/${configId}/history`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors du chargement de l'historique");
    throw new Error(msg);
  }
  return response.json();
}

/** Get all tax configuration change history */
export async function fetchAllChangeHistory(
  limit = 50,
  offset = 0
): Promise<PaginatedResponse<TaxConfigurationHistory>> {
  const response = await get(`${BASE_PATH}/changes?limit=${limit}&offset=${offset}`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors du chargement de l'historique");
    throw new Error(msg);
  }
  return response.json();
}

/** Notify affected hotels about a tax configuration change */
export async function notifyAffectedHotels(configId: string): Promise<TaxChangeNotification> {
  const response = await post(`${BASE_PATH}/${configId}/notify`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors de la notification des hôtels');
    throw new Error(msg);
  }
  return response.json();
}
